import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { adminDoctorsApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Calendar,
  Stethoscope,
  Activity,
  Users,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
};

const abbreviateName = (firstName, lastName) => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (!first && !last) return '—';
  if (!first) return last;
  return `${first.charAt(0).toUpperCase()}. ${last}`;
};

export const DoctorDetailPage = () => {
  const { doctorId } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientsTotal, setPatientsTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doctorId) return;
    const fetch = async () => {
      try {
        const [doctorData, statsData, patientsData] = await Promise.all([
          adminDoctorsApi.getDoctor(doctorId),
          adminDoctorsApi.getStats(doctorId).catch(() => ({ total_scans: 0 })),
          adminDoctorsApi.getPatients(doctorId).catch(() => ({ patients: [], total: 0 })),
        ]);
        setDoctor(doctorData);
        setStats(statsData);
        setPatients(patientsData.patients || []);
        setPatientsTotal(patientsData.total || 0);
      } catch (err) {
        toast.error('Doktor bilgileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [doctorId]);

  if (loading) {
    return (
      <DashboardLayout title="Doktor Detayı" subtitle="Yükleniyor...">
        <div className="text-center text-slate-400 p-8">Yükleniyor...</div>
      </DashboardLayout>
    );
  }

  if (!doctor) {
    return (
      <DashboardLayout title="Doktor Detayı" subtitle="Bulunamadı">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center text-slate-400" data-testid="doctor-not-found">
            Doktor bulunamadı
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={doctor.name || 'Doktor Detayı'} subtitle={doctor.email}>
      <div className="space-y-6" data-testid="doctor-detail-page">
        <Link
          to="/superadmin/doctors"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#5A67D8] transition-colors"
          data-testid="back-to-doctors-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Doktorlara geri dön
        </Link>

        {/* Doctor Info Card */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-gradient-to-r from-[#5A67D8] to-[#4C51BF]">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              Doktor Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow icon={<Stethoscope className="w-4 h-4" />} label="İsim" value={doctor.name} testid="doctor-name" />
              <InfoRow icon={<Mail className="w-4 h-4" />} label="E-posta" value={doctor.email} testid="doctor-email" />
              <InfoRow icon={<Phone className="w-4 h-4" />} label="Telefon" value={doctor.phone || '—'} testid="doctor-phone" />
              <InfoRow icon={<Building2 className="w-4 h-4" />} label="Klinik" value={doctor.clinic_name || '—'} testid="doctor-clinic" />
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Kayıt Tarihi" value={formatDate(doctor.created_at)} testid="doctor-created-at" />
              <div className="flex items-start gap-3 py-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500">
                  <Activity className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Durum</p>
                  <Badge variant={doctor.is_active ? 'default' : 'secondary'} className="mt-1" data-testid="doctor-status">
                    {doctor.is_active ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <StatCard
            icon={<Activity className="w-5 h-5" />}
            label="Toplam 4K Üretim"
            value={stats?.total_scans ?? 0}
            color="text-[#5A67D8]"
            bg="bg-[#E0D7FF]"
            testid="stat-total-scans"
          />
        </div>

        {/* Patients Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="py-4 px-6 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#5A67D8]" />
              Hastalar ({patientsTotal})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {patients.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="patients-empty">
                Bu doktorun henüz hastası yok
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="patients-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-6 py-3">Ad Soyad</th>
                      <th className="text-left font-medium px-6 py-3">Hasta ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {patients.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`patient-row-${p.id}`}>
                        <td className="px-6 py-3 font-medium text-slate-700" data-testid={`patient-name-${p.id}`}>
                          {abbreviateName(p.first_name, p.last_name)}
                        </td>
                        <td className="px-6 py-3 font-mono text-xs text-slate-500" data-testid={`patient-id-${p.id}`}>
                          {p.id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const InfoRow = ({ icon, label, value, testid }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 font-medium truncate" data-testid={testid}>{value}</p>
    </div>
  </div>
);

const StatCard = ({ icon, label, value, color, bg, testid }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-800" data-testid={testid}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default DoctorDetailPage;
